import { Pipe, PipeTransform } from '@angular/core';
import { extractPeriodLabel } from '../../../util/date';
import { Application } from '../application.model';
import { Workshop } from '../../workshop/workshop.model';
import { Course } from '../../course/course.model';
import { Camp } from '../../camp/camp.model';
import { isCourse } from '../../course/course.util';
import { activityRoleLabels } from '../../activity/lib/activity.util';
import { isWorkshop } from '../../workshop/workshop.util';

export function extractPeriod(application: Application) {
  const { camp, workshop, course } = application;
  const toExtract =
    camp?.schedule || workshop?.schedule || course?.camp?.schedule;

  return extractPeriodLabel(toExtract);
}

@Pipe({
  name: 'extractLocation',
  standalone: true,
})
export class ApplicationLocationPipe implements PipeTransform {
  transform(application: Application): string {
    if (application.workshop?.location) {
      return application.workshop.location.name;
    } else if (application.camp?.location) {
      return application.camp.location.name;
    } else if (application.course?.camp?.location) {
      return application.course.camp.location.name;
    }
    return '---';
  }
}

@Pipe({
  name: 'extractAvailabilityLocation',
  standalone: true,
})
export class AvailabilityLocationPipe implements PipeTransform {
  transform(target: Course | Camp | Workshop): string {
    if (isCourse(target)) {
      return target.camp?.location.name || '---';
    }
    return target.location.name || '---';
  }
}

@Pipe({
  name: 'extractCity',
  standalone: true,
})
export class ApplicationCityPipe implements PipeTransform {
  transform(application: Application): string {
    if (application.workshop?.location) {
      return application.workshop.location.city;
    } else if (application.camp?.location) {
      return application.camp.location.city;
    } else if (application.course?.camp?.location) {
      return application.course.camp.location.city;
    }
    return '---';
  }
}

@Pipe({
  name: 'extractAvailabilityCity',
  standalone: true,
})
export class AvailabilityCityPipe implements PipeTransform {
  transform(target: Course | Camp | Workshop): string {
    if (isCourse(target)) {
      return target.camp?.location.city || '---';
    }
    return target.location.city || '---';
  }
}

@Pipe({
  name: 'extractPeriod',
  standalone: true,
})
export class ApplicationPeriodPipe implements PipeTransform {
  transform(application: Application): string {
    return extractPeriod(application);
  }
}

@Pipe({
  name: 'extractPeriodLabel',
  standalone: true,
})
export class ApplicationPeriodLabelPipe implements PipeTransform {
  transform(application: Application): string {
    const { camp, course } = application;

    if (course && course.camp?.period) {
      return `${course.camp.period.label} ${course.camp.period.week}`;
    }
    if (camp?.period) {
      return `${camp.period.label} ${camp.period.week}`;
    }
    return '---';
  }
}

@Pipe({
  name: 'extractAvailabilityPeriodLabel',
  standalone: true,
})
export class AvailabilityPeriodLabelPipe implements PipeTransform {
  transform(target: Course | Camp | Workshop): string {
    if (isCourse(target)) {
      const period = target.camp?.period;
      if (!period) return '---';
      return `${target.camp.period.label} ${target.camp.period.week}`;
    }
    if (isWorkshop(target)) {
      return '---';
    }
    if (!target.period) return '---';
    return `${target.period.label} ${target.period.week}`;
  }
}

@Pipe({
  name: 'extractAvailabilityPeriod',
  standalone: true,
})
export class AvailabilityPeriodPipe implements PipeTransform {
  transform(target: Course | Camp | Workshop): string {
    if (isCourse(target)) {
      return extractPeriodLabel(target.camp?.schedule);
    }
    return extractPeriodLabel(target.schedule);
  }
}

@Pipe({
  name: 'extractRoleType',
  standalone: true,
})
export class ApplicationRoleTypePipe implements PipeTransform {
  transform(data: Application | Workshop | Course | Camp): string {
    return extractRoleTypes(data)
      .map((role) => activityRoleLabels[role])
      .join(', ');
  }
}

export function extractRoleTypes(data: Application | Workshop | Course | Camp) {
  return (
    (data instanceof Application
      ? data.roles
      : data.openSpotsInformation?.openLabels) ?? []
  );
}

@Pipe({
  name: 'extractThemeName',
  standalone: true,
})
export class ThemeNamePipe implements PipeTransform {
  transform(data: Application | Workshop | Course | Camp): string | undefined {
    const target = data instanceof Application ? data.target : data;
    return 'theme' in target ? target.theme?.name : undefined;
  }
}

export function isApplication(resource: unknown): resource is Application {
  return (
    resource instanceof Application ||
    (typeof resource === 'object' &&
      resource !== null &&
      (resource as any).certaintyLevel !== undefined)
  );
}
